import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import NotFound from '../ul/NotFound';

const generateChartData = (data) => {
    if (!data || data.length === 0) return null;

    const counts = data.reduce((acc, entry) => {
        const date = new Date(entry.createdAt);
        const day = date.toLocaleDateString('en-GB');
        acc[day] = (acc[day] || 0) + entry.count;
        return acc;
    }, {});

    const sortedData = Object.entries(counts)
        .map(([date, count]) => ({ x: date, y: count }))
        .sort((a, b) => new Date(a.x.split('/').reverse().join('-')) - new Date(b.x.split('/').reverse().join('-')));

    return {
        labels: sortedData.map((entry) => entry.x),
        values: sortedData.map((entry) => entry.y),
    };
};

const ChartComponent = ({ data, label, color }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const chartData = generateChartData(data);

    useEffect(() => {
        if (!chartData || !chartRef.current) return;

        // Initialize chart
        chartInstance.current = echarts.init(chartRef.current);

        const option = {
            tooltip: {
                trigger: 'axis',
                formatter: (params) => {
                    const { name, value } = params[0];
                    return `${value} ${label} on ${name}`;
                },
            },
            legend: {
                data: [label],
                top: 10,
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                name: 'Date',
                nameLocation: 'middle',
                nameGap: 30,
                data: chartData.labels,
                axisLabel: {
                    rotate: chartData.labels.length > 7 ? 45 : 0,
                },
            },
            yAxis: {
                type: 'value',
                name: 'Count',
                nameLocation: 'middle',
                nameGap: 40,
                min: 0,
            },
            series: [
                {
                    name: label,
                    type: 'line',
                    smooth: 0.4,
                    data: chartData.values,
                    lineStyle: {
                        color: color,
                        width: 2,
                    },
                    itemStyle: {
                        color: color,
                    },
                    areaStyle: {
                        color: `${color}50`,
                    },
                },
            ],
        };

        chartInstance.current.setOption(option);

        // Handle resize
        const handleResize = () => {
            chartInstance.current?.resize();
        };
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            chartInstance.current?.dispose();
        };
    }, [chartData, label, color]);

    if (!chartData) return <NotFound />;

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <div ref={chartRef} style={{ height: '350px', width: '100%' }} />
        </div>
    );
};

const ViewChart = ({ ViewData }) => <ChartComponent data={ViewData} label="Views" color="#3C7FFF" />;
const ClickChart = ({ ClickData }) => <ChartComponent data={ClickData} label="Clicks" color="#6f42c1" />;

export { ClickChart, ViewChart };